import type { DocDocument, ExcelContent, PowerPointContent, WordContent } from '@/lib/documents';

/** Retire le balisage HTML pour ne garder que le texte lisible. */
function stripHtml(html: string): string {
  if (!html) return '';
  if (typeof document === 'undefined') {
    return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  }
  const container = document.createElement('div');
  container.innerHTML = html;
  return (container.textContent || '').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * Extrait un texte exploitable d'un document Zifek (Word, Excel, PowerPoint)
 * pour alimenter la génération de questions par l'IA.
 */
export function extractDocText(doc: DocDocument, maxChars = 8000): string {
  const content = (doc.content || {}) as Record<string, unknown>;
  let text = '';

  if (doc.doc_type === 'word') {
    text = stripHtml((content as WordContent).html || '');
  } else if (doc.doc_type === 'excel') {
    const excel = content as ExcelContent;
    const sheets = excel.sheets && excel.sheets.length > 0
      ? excel.sheets
      : [{ name: 'Feuille 1', cells: excel.cells || {} }];
    const parts: string[] = [];
    sheets.forEach((sheet) => {
      const cells = Object.entries(sheet.cells || {})
        .filter(([, value]) => String(value || '').trim())
        .slice(0, 400)
        .map(([key, value]) => `${key}: ${String(value).trim()}`);
      if (cells.length > 0) parts.push(`[${sheet.name}]\n${cells.join('\n')}`);
    });
    text = parts.join('\n\n');
  } else if (doc.doc_type === 'powerpoint') {
    const slides = (content as PowerPointContent).slides || [];
    text = slides
      .map((slide, index) => {
        const body = stripHtml(slide.body || '');
        const title = slide.title || `Diapositive ${index + 1}`;
        return `${title}\n${body}`;
      })
      .join('\n\n');
  } else {
    // PDF : le contenu est un flux binaire, non exploitable en texte ici.
    text = '';
  }

  return text.replace(/\n{3,}/g, '\n\n').trim().slice(0, maxChars);
}

/** Vrai si le document contient assez de texte pour générer des questions. */
export function docHasText(doc: DocDocument): boolean {
  return extractDocText(doc, 200).length > 120;
}